import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePaperExamsList } from '../../api/hooks';
import { SchoolApi } from '../../api/client';
import type { PaperExamListRow } from '../../api/types';
import { Badge, Button, EmptyState, ErrorPanel, KpiCard, PageHeader, PagedFooter, PanelCard, PillTabs, Segmented, SortableTh, Spinner, Track, TH_CLASS } from '../../components/ui';
import { Download, FileCheck, List, Plus, SquareCheckBig, X } from 'lucide-react';
import { useSortableTable, SortColumn } from '../../lib/useSortableTable';
import { apiErrorText } from '../../lib/apiError';
import { downloadCsv, toCsv } from '../../lib/csv';
import usePagination from '../../lib/usePagination';
import RollNumbersModal from './components/RollNumbersModal';
import { expectedSheets, listStats, listSubtitle, paperStatus, sectionLetters, type PaperStatus } from './paperStats';

const COLUMNS: SortColumn<PaperExamListRow>[] = [
  { key: 'name', accessor: (e) => e.name },
  { key: 'questions', accessor: (e) => e.question_count, type: 'number' },
  { key: 'sheets', accessor: (e) => e.sheets.total, type: 'number' },
  { key: 'average', accessor: (e) => e.average ?? undefined, type: 'number' },
  { key: 'last_scan', accessor: (e) => e.last_scan_at, type: 'date' },
  { key: 'progress', accessor: (e) => {
    const exp = expectedSheets(e);
    return exp > 0 ? e.sheets.confirmed / exp : 0;
  }, type: 'number' },
];

type StatusKey = 'all' | PaperStatus;
type SortPick = 'recent' | 'progress' | 'name';

const STATUS_LABEL: Record<PaperStatus, string> = {
  review: 'Needs review', collect: 'Collecting sheets', marked: 'Marked', print: 'Ready to print',
};
const STATUS_TONE = { review: 'accent', collect: 'info', marked: 'success', print: 'neutral' } as const;
const SORT_DEFS: Record<SortPick, { key: string; dir: 'asc' | 'desc' }> = {
  recent: { key: 'last_scan', dir: 'desc' },
  progress: { key: 'progress', dir: 'asc' },
  name: { key: 'name', dir: 'asc' },
};

const TD = 'border-b border-hairline px-2.5 py-3 align-middle group-last:border-b-0';

const HOW_CARDS = [
  { title: 'Register the exam', body: 'Snap the blank paper; questions and the answer key are read for you.' },
  { title: 'Print the answer sheet', body: 'One bubble sheet per student, with roll number and a QR code.' },
  { title: 'Photograph filled sheets', body: 'Upload photos or a PDF; marks are read and graded automatically.' },
];

const HOW_STEPS = [
  'Register the exam from a photo of the paper.',
  'Print the answer sheet and photocopy it for the class.',
  'Photograph the filled-in sheets to scan and grade them.',
];

export function sheetFilename(examName: string): string {
  const slug = examName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'exam';
  return `answer-sheet-${slug}.pdf`;
}

function formatLastScan(iso: string | null): string {
  if (!iso) return 'Not scanned';
  return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Router state the wizard hands over after saving an exam. */
interface ListNotice { notice?: string; examId?: number; examName?: string }

const CHIP_BASE = 'rounded-full px-2 py-[3px] font-mono text-xs font-semibold';

/** The sheets cell: "confirmed / expected" with a sub line, over a two-layer track. */
function SheetsCell({ e }: { e: PaperExamListRow }) {
  const exp = expectedSheets(e);
  const review = e.sheets_needs_review ?? 0;
  const up = e.sheets.total;
  const conf = e.sheets.confirmed;
  let sub = 'All confirmed';
  let subCls = 'text-success';
  if (review > 0) { sub = `${review} to review`; subCls = 'text-accent-ink'; }
  else if (up === 0) { sub = 'Not printed yet'; subCls = 'text-muted'; }
  else if (conf < up) { sub = `${up - conf} to confirm`; subCls = 'text-muted'; }
  else if (up < exp) { sub = `${exp - up} not in yet`; subCls = 'text-muted'; }
  const pct = (n: number) => (exp > 0 ? Math.min(100, (n / exp) * 100) : 0);
  return (
    <div className="min-w-[10rem]">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="whitespace-nowrap font-mono font-semibold tabular-nums text-ink">
          {conf} <span className="font-medium text-muted">/ {exp}</span>
        </span>
        <span className={`whitespace-nowrap text-xs ${subCls}`}>{sub}</span>
      </div>
      <div aria-hidden className="relative h-2 overflow-hidden rounded-[4px] bg-track">
        <span className="absolute inset-y-0 left-0 rounded-[4px] bg-line-hover" style={{ width: `${pct(up)}%` }} />
        <span className="absolute inset-y-0 left-0 rounded-[4px] bg-slate" style={{ width: `${pct(conf)}%` }} />
      </div>
    </div>
  );
}

/** Academics → Paper exams (/exams/paper). Every exam sat on paper: the
 * printable answer sheet, scanning the filled sheets and the exam report
 * (spec §2 step 3, §7; plan B Task 3). */
export default function PaperExamsPage() {
  const navigate = useNavigate();
  const location = useLocation() as { state?: ListNotice | null };
  const [notice, setNotice] = useState<ListNotice | null>(location.state?.notice ? location.state : null);

  const q = usePaperExamsList();
  const { rows, sort, toggle, setSort } = useSortableTable(q.data?.exams, COLUMNS, { key: 'last_scan', dir: 'desc' });

  const [statusFilter, setStatusFilter] = useState<StatusKey>('all');
  const [gradeFilter, setGradeFilter] = useState('all');

  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const [rollOpen, setRollOpen] = useState(false);
  const [rollGrade, setRollGrade] = useState<number | null>(null);
  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades(), enabled: rollOpen });

  async function handleDownload(id: number, name: string) {
    setDownloadError(null);
    setDownloadingId(id);
    try {
      await SchoolApi.downloadAnswerSheet(id, sheetFilename(name));
    } catch (err) {
      setDownloadError(apiErrorText(err, 'Could not download the answer sheet. Try again.'));
    } finally {
      setDownloadingId(null);
    }
  }

  const exams = q.data?.exams ?? [];
  const stats = useMemo(() => listStats(exams), [exams]);
  const statusCounts = useMemo(() => {
    const c = { all: exams.length, review: 0, collect: 0, marked: 0, print: 0 };
    for (const e of exams) c[paperStatus(e)] += 1;
    return c;
  }, [exams]);
  const grades = useMemo(() => {
    const seen = new Map<number, string>();
    for (const e of exams) if (e.grade) seen.set(e.grade.id, e.grade.name);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [exams]);
  const shown = useMemo(
    () => rows.filter((e) => (statusFilter === 'all' || paperStatus(e) === statusFilter)
      && (gradeFilter === 'all' || String(e.grade?.id) === gradeFilter)),
    [rows, statusFilter, gradeFilter],
  );
  const pg = usePagination(shown, 10, `${statusFilter}|${gradeFilter}|${sort?.key}|${sort?.dir}`);
  const sortPick = (Object.keys(SORT_DEFS) as SortPick[]).find((k) => SORT_DEFS[k].key === sort?.key) ?? ('' as SortPick);
  const ready = !q.isLoading && !q.isError && !!q.data;

  // Roll numbers: the dialog needs a grade; default to the first exam's, else the first school grade.
  const rollGrades = (gradesQ.data?.grades ?? []).map((g) => ({ id: g.id, name: g.name }));
  const rollGradeId = rollGrade ?? exams.find((e) => e.grade)?.grade?.id ?? rollGrades[0]?.id ?? null;
  const rollGradeName = rollGrades.find((g) => g.id === rollGradeId)?.name
    ?? exams.find((e) => e.grade?.id === rollGradeId)?.grade?.name ?? '';

  /** Client-side CSV of the rows in view (every page), no endpoint needed. */
  function handleExport() {
    const head = ['Exam', 'Subject', 'Grade', 'Sections', 'Questions', 'Sheets expected', 'Sheets uploaded', 'Sheets confirmed', 'Needs review', 'Status', 'Average %', 'Last scan'];
    const body = shown.map((e) => [
      e.name, e.subject?.name ?? '', e.grade?.name ?? '', sectionLetters(e), e.question_count,
      e.sheets_expected ?? '', e.sheets.total, e.sheets.confirmed, e.sheets_needs_review ?? '',
      STATUS_LABEL[paperStatus(e)], e.average ?? '', e.last_scan_at ?? '',
    ]);
    downloadCsv('paper-exams.csv', toCsv(head, body));
  }

  // Once the grades have loaded (or failed) with none to pick, the button says so instead of spinning.
  const noGrade = rollGradeId == null && rollOpen && gradesQ.isSuccess;
  const gradesFailed = rollGradeId == null && rollOpen && gradesQ.isError;
  const examNoun = stats.examsNeedingReview === 1 ? 'exam' : 'exams';

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        eyebrow="Academics · Paper exams"
        title="Paper exams"
        subtitle={ready ? listSubtitle(stats) : 'Print answer sheets, scan them back and mark them automatically.'}
        actions={(
          <>
            <Button
              variant="ghost" icon={<List size={18} strokeWidth={1.8} />}
              loading={rollOpen && rollGradeId == null && !noGrade && !gradesFailed}
              disabled={noGrade} title={noGrade ? 'No grades are set up yet' : undefined}
              onClick={() => setRollOpen(true)}>
              Roll numbers
            </Button>
            <Button variant="ghost" icon={<Download size={18} strokeWidth={1.8} />} disabled={!ready || shown.length === 0} onClick={handleExport}>
              Export
            </Button>
            <Button icon={<Plus size={18} strokeWidth={1.8} />} onClick={() => navigate('/exams/paper/new')}>New paper exam</Button>
          </>
        )}
      />

      {notice && (
        <div role="status" className="mb-[18px] flex flex-wrap items-center gap-3 rounded-[14px] border border-accent-line bg-accent-bg px-3.5 py-3 text-sm text-warm-ink">
          <SquareCheckBig size={18} strokeWidth={1.8} aria-hidden className="flex-none text-accent-ink" />
          <span className="min-w-0 flex-1">
            {notice.examName ? (
              <>
                <strong>Exam saved.</strong> Print the answer sheet for <strong>{notice.examName}</strong> next, then photocopy it for the class.
              </>
            ) : notice.notice}
          </span>
          {notice.examId != null && (
            <Button
              variant="ghost" size="sm"
              loading={downloadingId === notice.examId}
              onClick={() => handleDownload(notice.examId as number, notice.examName ?? 'exam')}>
              Answer sheet (PDF)
            </Button>
          )}
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            className="grid h-9 w-9 flex-none place-items-center rounded-[10px] border border-accent-line bg-transparent p-0 text-current hover:bg-navy/[.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            <X size={18} strokeWidth={1.8} aria-hidden />
          </button>
        </div>
      )}
      {gradesFailed && (
        <p role="alert" className="mb-3 text-xs text-danger">
          Could not load the grades.{' '}
          <button type="button" className="font-semibold underline" onClick={() => { void gradesQ.refetch(); }}>Try again</button>
        </p>
      )}
      {noGrade && <p className="mb-3 text-xs text-muted">Roll numbers need a grade. No grades are set up yet.</p>}
      {downloadError && <p className="mb-3 text-xs text-danger">{downloadError}</p>}

      {ready && exams.length > 0 && (
        <>
          <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-4">
            <KpiCard
              size="lg" label="Paper exams" value={stats.exams}
              chip={stats.newThisMonth > 0 ? <span className={`${CHIP_BASE} bg-success-bg text-success`}>+{stats.newThisMonth} this month</span> : undefined}
              sub={`${stats.subjects} ${stats.subjects === 1 ? 'subject' : 'subjects'}${stats.gradeSpan ? ` · ${stats.gradeSpan.includes('–') ? 'grades' : 'grade'} ${stats.gradeSpan}` : ''}`}
            />
            <KpiCard
              size="lg" label="Sheets scanned" value={stats.uploaded}
              unit={stats.expected > 0 ? <span className="text-[18px] text-muted"> / {stats.expected}</span> : undefined}
              chip={stats.expected > 0 ? <span className="font-mono text-xs font-medium text-muted">{Math.round((stats.uploaded / stats.expected) * 100)}%</span> : undefined}
              track={stats.expected > 0 ? <Track value={(stats.uploaded / stats.expected) * 100} label="Sheets scanned" /> : undefined}
              sub={`${stats.confirmed} confirmed as results`}
            />
            <KpiCard
              size="lg" label="Needs review" accentBorder
              chip={stats.examsNeedingReview > 0 ? <span className={`${CHIP_BASE} bg-accent-bg text-accent-ink`}>{stats.examsNeedingReview} {examNoun}</span> : undefined}
              sub="Marked twice, unclear or unknown roll number">
              <div className="mt-2 whitespace-nowrap font-mono text-[38px] font-semibold leading-none tracking-[-0.04em] text-accent-ink">
                {stats.needsReview == null ? '—' : stats.needsReview}
              </div>
            </KpiCard>
            <KpiCard
              size="lg" label="Avg. paper score"
              value={stats.avgScore == null ? '—' : stats.avgScore.toFixed(1)}
              unit={stats.avgScore == null ? undefined : '%'}
              chip={stats.avgDelta != null && Math.round(stats.avgDelta * 10) !== 0 ? (
                <span className={`${CHIP_BASE} ${stats.avgDelta > 0 ? 'bg-success-bg text-success' : 'bg-danger-bg text-danger-ink'}`}>
                  {stats.avgDelta > 0 ? '↑' : '↓'} {Math.abs(stats.avgDelta).toFixed(1)}
                </span>
              ) : undefined}
              sub={stats.markedExams > 0
                ? `Across ${stats.markedExams} fully marked ${stats.markedExams === 1 ? 'exam' : 'exams'}`
                : 'No fully marked exams yet'}
            />
          </div>

          <section aria-labelledby="paper-how" className="mb-[18px]">
            <h2 id="paper-how" className="sr-only">How paper exams work</h2>
            <ol className="grid gap-[18px] min-[821px]:grid-cols-3">
              {HOW_CARDS.map((c, i) => (
                <li key={c.title} className="flex items-start gap-3.5 rounded-[18px] border border-line bg-surface p-[18px]">
                  <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-mist font-mono text-[13px] font-semibold text-muted-2">{i + 1}</span>
                  <span>
                    <span className="block font-semibold text-ink">{c.title}</span>
                    <span className="text-[13px] text-muted">{c.body}</span>
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </>
      )}

      <PanelCard>
        {q.isLoading ? (
          <div className="flex justify-center py-8"><Spinner size="md" /></div>
        ) : q.isError || !q.data ? (
          <ErrorPanel title="Could not load paper exams" onRetry={() => q.refetch()} />
        ) : q.data.exams.length === 0 ? (
          <EmptyState
            icon={<FileCheck size={22} strokeWidth={1.8} />}
            title="No paper exams yet"
            body={
              <ol className="list-decimal space-y-1 pl-4 text-left">
                {HOW_STEPS.map((text) => <li key={text}>{text}</li>)}
              </ol>
            }
            action={<Button onClick={() => navigate('/exams/paper/new')}>Create a paper exam</Button>}
          />
        ) : (
          <>
            <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
              <PillTabs<StatusKey>
                label="Filter by status" scroll badge height={36}
                value={statusFilter} onChange={setStatusFilter}
                options={[
                  { key: 'all', label: 'All', count: statusCounts.all },
                  { key: 'review', label: 'Needs review', count: statusCounts.review },
                  { key: 'collect', label: 'Collecting', count: statusCounts.collect },
                  { key: 'marked', label: 'Marked', count: statusCounts.marked },
                  { key: 'print', label: 'Ready to print', count: statusCounts.print },
                ]} />
              <Segmented<SortPick>
                label="Sort by" value={sortPick} onChange={(k) => setSort(SORT_DEFS[k])}
                options={[
                  { key: 'recent', label: 'Last scan' },
                  { key: 'progress', label: 'Least done' },
                  { key: 'name', label: 'Name A–Z' },
                ]} />
            </div>
            {grades.length > 1 && (
              <div className="mb-[18px] flex flex-wrap items-center gap-3">
                <span className="text-[13px] text-muted">Grade</span>
                <PillTabs<string>
                  label="Filter by grade" scroll height={36} value={gradeFilter} onChange={setGradeFilter}
                  options={[{ key: 'all', label: 'All grades' }, ...grades.map(([id, name]) => ({ key: String(id), label: name }))]} />
              </div>
            )}
            {pg.total === 0 ? (
              <EmptyState
                icon={<FileCheck size={22} strokeWidth={1.8} />}
                title="No paper exams here"
                body="Nothing matches these filters. Try another status or grade, or create a paper exam."
                action={<Button onClick={() => navigate('/exams/paper/new')}>Create a paper exam</Button>}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[940px] border-collapse text-left text-sm">
                  <thead>
                    <tr>
                      <SortableTh columnKey="name" sort={sort} onToggle={toggle} className={TH_CLASS}>Exam</SortableTh>
                      <SortableTh columnKey="questions" sort={sort} onToggle={toggle} align="right" className={TH_CLASS}>Questions</SortableTh>
                      <SortableTh columnKey="sheets" sort={sort} onToggle={toggle} help="sheet_status" className={`w-[22%] ${TH_CLASS}`}>Sheets confirmed</SortableTh>
                      <th scope="col" className={TH_CLASS}>Status</th>
                      <SortableTh columnKey="average" sort={sort} onToggle={toggle} align="right" className={TH_CLASS}>Average</SortableTh>
                      <SortableTh columnKey="last_scan" sort={sort} onToggle={toggle} className={TH_CLASS}>Last scan</SortableTh>
                      <th scope="col" className={`${TH_CLASS} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((e) => {
                      const status = paperStatus(e);
                      const letters = sectionLetters(e);
                      return (
                        <tr key={e.id} className="group transition-colors [&:hover>td]:bg-surface-2">
                          <td className={TD}>
                            <Link to={`/exams/paper/${e.id}/sheets`} className="flex items-center gap-3 no-underline">
                              <span className="grid h-9 w-9 flex-none place-items-center rounded-[12px] bg-mist text-slate">
                                <FileCheck size={18} strokeWidth={1.8} aria-hidden />
                              </span>
                              <span className="min-w-0">
                                <p className="break-words font-semibold text-ink">{e.name}</p>
                                <span className="block text-xs text-muted">
                                  {[e.subject?.name, e.grade?.name, letters].filter(Boolean).join(' · ')}
                                </span>
                              </span>
                            </Link>
                          </td>
                          <td className={`${TD} text-right font-mono font-medium tabular-nums text-ink`}>{e.question_count}</td>
                          <td className={TD}><SheetsCell e={e} /></td>
                          <td className={TD}><Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge></td>
                          <td className={`${TD} text-right font-mono font-medium tabular-nums text-ink`}>
                            {e.average != null ? `${e.average.toFixed(1)}%` : '—'}
                          </td>
                          <td className={`${TD} whitespace-nowrap font-mono text-[13px] font-medium text-muted`}>{formatLastScan(e.last_scan_at)}</td>
                          <td className={TD}>
                            <div className="flex flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap">
                              <Button
                                variant="ghost" size="sm" className="whitespace-nowrap" aria-label="Answer sheet (PDF)"
                                icon={<Download size={18} strokeWidth={1.8} />}
                                loading={downloadingId === e.id}
                                onClick={() => handleDownload(e.id, e.name)}>
                                PDF
                              </Button>
                              <Button
                                variant={status === 'review' ? 'secondary' : 'ghost'} size="sm" className="whitespace-nowrap"
                                onClick={() => navigate(`/exams/paper/${e.id}/sheets`)}>
                                {status === 'review' ? 'Review sheets' : 'Scan sheets'}
                              </Button>
                              <Button
                                variant="ghost" size="sm" className="whitespace-nowrap"
                                onClick={() => navigate(`/exams/report/${e.id}`)}>
                                Report
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <PagedFooter className="mt-4" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} />
            <p className="mt-3.5 text-xs text-muted">Light bar = sheets uploaded · dark bar = confirmed as results.</p>
          </>
        )}
      </PanelCard>

      <RollNumbersModal
        gradeId={rollGradeId ?? 0}
        gradeName={rollGradeName}
        isOpen={rollOpen && rollGradeId != null}
        onClose={() => setRollOpen(false)}
        grades={rollGrades}
        onGradeChange={setRollGrade}
      />
    </div>
  );
}
